import React, { useState } from "react";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
// framer-motion removed from the homepage critical path. Testimonial
// switching uses a CSS keyframe fade keyed on the active index instead.

export default function TestimonialsSection() {
  const [current, setCurrent] = useState(0);

  const { data: testimonials = [] } = useQuery({
    queryKey: ["testimonials-featured"],
    queryFn: () => base44.entities.Testimonials.filter({ featured: true }),
    initialData: [],
  });

  if (!testimonials.length) return null;

  const prev = () => setCurrent((c) => (c - 1 + testimonials.length) % testimonials.length);
  const next = () => setCurrent((c) => (c + 1) % testimonials.length);
  const t = testimonials[current];

  return (
    <section className="py-20 lg:py-28 bg-background">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-3">Client Reviews</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            What Baltimore Homeowners Say
          </h2>
        </div>

        <div className="relative bg-card border border-border rounded-2xl p-8 md:p-12 shadow-sm">
          <Quote className="w-12 h-12 text-accent/20 mb-6" />

          <div
            key={current}
            className="tn-fade-in"
          >
              <div className="flex gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-accent text-accent" />
                ))}
              </div>
              <p className="font-body text-lg text-foreground leading-relaxed mb-8 italic">
                "{t.text}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/15 rounded-full flex items-center justify-center">
                  <span className="font-heading font-bold text-accent text-sm">{t.name[0]}</span>
                </div>
                <div>
                  <div className="font-body font-semibold text-foreground">{t.name}</div>
                  <div className="font-body text-sm text-muted-foreground">{t.location}</div>
                </div>
              </div>
          </div>

          {/* Navigation */}
          {testimonials.length > 1 && (
            <div className="absolute right-8 bottom-8 flex gap-2">
              <button onClick={prev} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary hover:border-accent/30 transition-colors">
                <ChevronLeft className="w-4 h-4 text-muted-foreground" />
              </button>
              <button onClick={next} className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary hover:border-accent/30 transition-colors">
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}
        </div>

        {/* Dots */}
        {testimonials.length > 1 && (
          <div className="flex justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-accent w-6" : "bg-border"}`}
              />
            ))}
          </div>
        )}

        <div className="mt-8 text-center">
          <a
            href="https://www.google.com/maps/place/Top-Notch+Contractors"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-accent transition-colors border border-border rounded-full px-5 py-2"
          >
            ⭐ Read more reviews on Google
          </a>
        </div>
      </div>
    </section>
  );
}